/**
 * of-adapter-confirm.js
 *
 * Modal Add to Cart confirmation for 230V products whose plug needs a
 * country-appropriate adapter (tag: adapter-required-230v, first consumer
 * the K.2200-2 matrix). v2 2026-08-11: Rhett rejected the two-tap label
 * swap as inconsistent and easy to miss (and Empire styles the confirming
 * state green); this version intercepts the PDP form submit and shows a
 * blocking dialog with the adapter graphic, oxblood-styled, with an
 * explicit accept button that resumes the add.
 *
 * product-form.liquid arms this with data-of-adapter-confirm and supplies
 * the graphic URL via data-of-adapter-image. Shows on EVERY add attempt on
 * purpose; the accept path re-submits the same form so Empire's own add
 * handlers (flyout, cart refresh) run unchanged.
 *
 * v3 2026-08-13 (first consumer the K.2200-22CE CE-plug page): the modal
 * copy is now overridable per product via data-of-adapter-title /
 * data-of-adapter-text / data-of-adapter-alt, so native-plug 230V products
 * can confirm voltage without claiming an adapter is required. Overrides
 * are applied as plain text (textContent); absent attributes keep the
 * original adapter copy, so K.2200-2 and K.2272-2 render unchanged.
 *
 * GA4: of_adapter_confirm_shown (first show per page view),
 * of_adapter_confirm_accepted, of_adapter_confirm_cancelled.
 * Fails open without OFTrack.
 */
(function () {
  'use strict';

  function track(name) {
    try {
      if (window.OFTrack && window.OFTrack.event) {
        window.OFTrack.event(name, { event_category: 'otto_frei' });
      }
    } catch (e) { /* analytics must never block adding to cart */ }
  }

  var modal = null;
  var activeForm = null;
  var shownOnce = false;

  function buildModal(imageUrl, copy) {
    var wrap = document.createElement('div');
    wrap.className = 'of-adapter-modal';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('aria-labelledby', 'of-adapter-modal-title');
    wrap.innerHTML =
      '<div class="of-adapter-modal__overlay" data-adapter-cancel></div>' +
      '<div class="of-adapter-modal__dialog">' +
        '<div class="of-adapter-modal__header" id="of-adapter-modal-title">230V Kit: Plug Adapter Required</div>' +
        '<div class="of-adapter-modal__body">' +
          (imageUrl ? '<img class="of-adapter-modal__img" src="' + imageUrl + '" alt="230V only. The NEMA 5-15 style plug needs a country-appropriate 230V plug adapter. Do not connect to 110/120V outlets.">' : '') +
          '<p class="of-adapter-modal__text">This kit runs on <strong>230V only</strong>. Its plug is the North American NEMA 5-15 shape, so you will need a plug adapter for your 230V outlet. <strong>It cannot be used on 110/120V power.</strong></p>' +
          '<p class="of-adapter-modal__addon" data-adapter-addon hidden></p>' +
        '</div>' +
        '<div class="of-adapter-modal__actions">' +
          '<button type="button" class="of-adapter-modal__accept" data-adapter-accept>I understand, add to cart</button>' +
          '<button type="button" class="of-adapter-modal__cancel" data-adapter-cancel>Cancel</button>' +
        '</div>' +
      '</div>';
    // Per-product copy overrides (v3). Plain text on purpose: attribute
    // values must never be injected as HTML.
    if (copy) {
      if (copy.title) {
        wrap.querySelector('.of-adapter-modal__header').textContent = copy.title;
      }
      if (copy.text) {
        wrap.querySelector('.of-adapter-modal__text').textContent = copy.text;
      }
      if (copy.alt) {
        var img = wrap.querySelector('.of-adapter-modal__img');
        if (img) img.alt = copy.alt;
      }
    }
    document.body.appendChild(wrap);

    wrap.addEventListener('click', function (e) {
      if (e.target.hasAttribute('data-adapter-cancel')) {
        close();
        track('of_adapter_confirm_cancelled');
      }
      if (e.target.hasAttribute('data-adapter-accept')) {
        var form = activeForm;
        close();
        track('of_adapter_confirm_accepted');
        if (form) {
          form.dataset.ofAdapterAccepted = '1';
          if (form.requestSubmit) { form.requestSubmit(); } else { form.submit(); }
        }
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal && modal.classList.contains('is-open')) {
        close();
        track('of_adapter_confirm_cancelled');
      }
    });
    return wrap;
  }

  // Reflects the PDP "Frequently bought together" checkbox (the MAMH-1
  // hanger add-on from of-kit-upgrade.js) inside the modal, so the
  // confirmation states everything the accept will actually add. Re-read
  // on every open: the checkbox can change between add attempts. Fails
  // silent and hidden if the kit-upgrade surface is absent.
  function refreshAddonLine() {
    if (!modal) return;
    var addonEl = modal.querySelector('[data-adapter-addon]');
    if (!addonEl) return;
    var checked = false;
    var priceCents = null;
    var addonTitle = 'Foredom MAMH-1 Motor Hanger';
    try {
      var cb = document.querySelector('#of-kit-upgrade .of-kit-upgrade__checkbox');
      checked = !!(cb && cb.checked);
      if (checked) {
        var dataEl = document.getElementById('of-kit-upgrade-data');
        var cfg = dataEl ? JSON.parse(dataEl.textContent) : null;
        if (cfg && typeof cfg.launchPriceCents === 'number') priceCents = cfg.launchPriceCents;
        if (cfg && cfg.addOn && cfg.addOn.title) addonTitle = cfg.addOn.title;
      }
    } catch (e) { checked = false; }
    if (!checked) { addonEl.hidden = true; return; }
    addonEl.textContent = 'Also adding: ' + addonTitle
      + (priceCents !== null ? ' at $' + (priceCents / 100).toFixed(2) + ' with this kit' : '')
      + '.';
    addonEl.hidden = false;
  }

  function open(form, imageUrl, copy) {
    if (!modal) modal = buildModal(imageUrl, copy);
    refreshAddonLine();
    activeForm = form;
    modal.classList.add('is-open');
    document.documentElement.classList.add('of-adapter-modal-lock');
    var accept = modal.querySelector('[data-adapter-accept]');
    if (accept) accept.focus();
    if (!shownOnce) { shownOnce = true; track('of_adapter_confirm_shown'); }
  }

  function close() {
    if (!modal) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('of-adapter-modal-lock');
    activeForm = null;
  }

  function initForm(form) {
    if (form.dataset.ofAdapterConfirmInit === '1') return;
    var btn = form.querySelector('[data-product-atc]');
    if (!btn || !btn.hasAttribute('data-of-adapter-confirm')) return;
    form.dataset.ofAdapterConfirmInit = '1';
    var imageUrl = btn.getAttribute('data-of-adapter-image') || '';
    var copy = {
      title: btn.getAttribute('data-of-adapter-title') || '',
      text: btn.getAttribute('data-of-adapter-text') || '',
      alt: btn.getAttribute('data-of-adapter-alt') || ''
    };
    // Variant scope (2026-08-12): data-of-adapter-variants, when present,
    // lists the ONLY variant ids the modal applies to (the K.2272-2 matrix
    // arms just its FCH-2 child; the AUS and CE children ship native plugs).
    // Absent attribute = every variant, which is K.2200-2's behavior.
    // Membership is checked at submit time against the form's live [name=id]
    // input, so variant switches need no re-arming.
    var scopeAttr = btn.getAttribute('data-of-adapter-variants') || '';
    var scopedIds = scopeAttr ? scopeAttr.split(',').filter(Boolean) : null;

    form.addEventListener('submit', function (e) {
      if (form.dataset.ofAdapterAccepted === '1') {
        delete form.dataset.ofAdapterAccepted;
        return; // accepted path: let Empire's handlers run
      }
      if (scopedIds) {
        var idInput = form.querySelector('[name="id"]');
        var currentId = idInput ? String(idInput.value) : '';
        if (scopedIds.indexOf(currentId) === -1) return; // native-plug variant: no modal
      }
      e.preventDefault();
      e.stopPropagation();
      open(form, imageUrl, copy);
    }, true);
  }

  function init(root) {
    (root || document).querySelectorAll('form[action*="/cart/add"]').forEach(initForm);
  }

  // F3 (2026-09-06 review): Quick Shop fetches ?view=quickshop and splices the
  // product form into the CURRENT document long after DOMContentLoaded, so a
  // one-shot scan at load left every quickshop form unarmed and a 230V machine
  // went into the cart from a collection, search or homepage grid with no
  // dialog at all. Re-scan whenever a subtree carrying a form is inserted.
  // initForm is idempotent (it stamps data-of-adapter-confirm-init on the
  // form), so re-scanning an already-armed page costs one querySelectorAll.
  //
  // F6 (2026-09-17 perf review): this file and of-variant-confirm.js ran
  // byte-identical body observers, so every node Judge.me, Klaviyo, Swym and
  // the twelve web pixels insert woke two callbacks and ran two subtree
  // queries. Whichever of the two files boots first installs the observer on
  // window.__ofFormWatch and the other subscribes to it; the code below is
  // deliberately identical in both, so neither file depends on the other's
  // presence. getElementsByTagName is a live-collection lookup rather than
  // querySelector's full selector match, and .length is all this test needs.
  function onInjectedForms() { init(); }

  function watchForInjectedForms() {
    if (!window.MutationObserver || !document.body) return;
    var watch = window.__ofFormWatch;
    if (!watch) {
      watch = window.__ofFormWatch = { listeners: [] };
      var scheduled = false;
      var rescan = function () {
        scheduled = false;
        for (var k = 0; k < watch.listeners.length; k++) {
          try { watch.listeners[k](); } catch (e) { /* one guard must not stop the other */ }
        }
      };
      new MutationObserver(function (records) {
        if (scheduled) return;
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var el = added[j];
            if (!el || el.nodeType !== 1) continue;
            if (el.tagName === 'FORM' || el.getElementsByTagName('form').length) {
              scheduled = true;
              if (window.requestAnimationFrame) window.requestAnimationFrame(rescan);
              else setTimeout(rescan, 0);
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
    if (watch.listeners.indexOf(onInjectedForms) === -1) watch.listeners.push(onInjectedForms);
  }

  function boot() {
    init();
    watchForInjectedForms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Theme-editor section reloads re-render the form from scratch.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
